import {
  Activity,
  AlertCircle,
  BookOpen,
  Compass,
  Database,
  FileEdit,
  GitBranch,
  Inbox,
  Layers,
  Library,
  ListTree,
  Network,
  Puzzle,
  Sparkles,
  Sun,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink } from 'react-router-dom';

import { useHasRole } from '@/lib/auth/AuthProvider';
import { cn } from '@/lib/cn';

interface NavGroup {
  label: string;
  items: NavItem[];
}

interface NavItem {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}

/**
 * Spec S03 §"Studio shell" — three groups of nav items, group visibility
 * gated by role. Sidebar is desktop-only (parent shell enforces 1280px).
 */
export function StudioSidebar() {
  const { t } = useTranslation('studio');
  const isDE = useHasRole('data_analyst_domain_expert');

  const groups: NavGroup[] = [
    {
      label: t('nav.groups.pipeline'),
      items: [
        { to: '/studio/morning', icon: Sun, label: t('nav.morning') },
        { to: '/studio/pipeline-runs', icon: Activity, label: t('nav.supervision') },
        { to: '/studio/sources', icon: Database, label: t('nav.sources') },
        { to: '/studio/errors', icon: AlertCircle, label: t('nav.errors') },
      ],
    },
  ];

  if (isDE) {
    groups.push({
      label: t('nav.groups.governance'),
      items: [
        { to: '/studio/ontology', icon: Network, label: t('nav.ontology') },
        { to: '/studio/ontology/browser', icon: ListTree, label: t('nav.ontologyBrowser') },
        { to: '/studio/explorer', icon: Compass, label: t('nav.explorer') },
        { to: '/studio/ontology/sparql', icon: Database, label: t('nav.sparqlEditor') },
        { to: '/studio/inference', icon: GitBranch, label: t('nav.inference') },
        { to: '/studio/skills', icon: Sparkles, label: t('nav.skills') },
      ],
    });
  }

  groups.push({
    label: t('nav.groups.input'),
    items: [
      { to: '/studio/ingest', icon: Inbox, label: t('nav.ingest', 'Ingest') },
      { to: '/studio/extension', icon: Puzzle, label: t('nav.extension', 'Extension') },
      { to: '/studio/curation', icon: FileEdit, label: t('nav.curation') },
      { to: '/studio/gis', icon: Layers, label: t('nav.gis') },
      { to: '/studio/help', icon: BookOpen, label: t('nav.help') },
    ],
  });

  return (
    <aside className="hidden h-full w-60 flex-col border-r border-slate-200 bg-white lg:flex">
      <Link
        to="/studio/morning"
        className="flex h-14 items-center gap-2 border-b border-slate-200 px-4 text-h2 font-semibold text-brand-900"
      >
        <Library className="h-5 w-5 text-accent" aria-hidden />
        {t('shell')}
      </Link>
      <nav className="flex-1 overflow-y-auto px-3 py-4" aria-label="Studio navigation">
        {groups.map((group) => (
          <div key={group.label} className="mb-4">
            <p className="mb-1 px-2 text-badge uppercase tracking-wider text-slate-500">
              {group.label}
            </p>
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-2 rounded px-2 py-1.5 text-body transition-colors',
                        isActive
                          ? 'bg-brand-50 font-medium text-brand-900'
                          : 'text-slate-700 hover:bg-slate-100',
                      )
                    }
                  >
                    <item.icon className="h-3.5 w-3.5" aria-hidden />
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      <PoFootnote />
    </aside>
  );
}

function PoFootnote() {
  const { t } = useTranslation('studio');
  const isPO = useHasRole('data_analyst_domain_expert');
  if (!isPO) return null;
  return (
    <div className="border-t border-slate-200 px-3 py-2 text-small text-slate-500">
      {t('poFootnote')}
    </div>
  );
}
